import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Building2, Globe2, Smartphone, Banknote, Coins, Copy, Check,
  Upload, FileImage, AlertTriangle, Sparkles, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const METHOD_META = {
  bank_transfer: { label: "Bank Transfer (NCB / JN)", Icon: Building2, blurb: "Local Jamaican bank transfer" },
  wire:          { label: "Wire Transfer (International)", Icon: Globe2, blurb: "SWIFT / IBAN — global" },
  lynk_ja:       { label: "Lynk JA", Icon: Smartphone, blurb: "Jamaican mobile money" },
  cash:          { label: "Cash on Completion", Icon: Banknote, blurb: "Pay in person" },
  crypto:        { label: "Crypto (BTC / ETH / USDT)", Icon: Coins, blurb: "Bitcoin, Ethereum, USDT-TRC20" },
};

function CopyChip({ value, label = "Copy" }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button
      data-testid="manual-pay-copy"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); }
        catch { /* noop */ }
      }}
      className="inline-flex items-center gap-1.5 text-xs text-gold/80 hover:text-gold border border-gold/30 hover:border-gold/60 rounded-md px-2 py-1 transition-colors"
    >
      {done ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {done ? "Copied" : label}
    </button>
  );
}

function InstructionsBlock({ method, instructions }) {
  if (!instructions) return null;
  if (method === "lynk_ja") {
    return (
      <div className="space-y-2 text-sm text-white/85">
        <div className="flex items-center justify-between gap-3">
          <div><span className="text-white/60">Handle: </span><span className="text-gold font-medium">{instructions.handle || "—"}</span></div>
          <CopyChip value={instructions.handle} label="Copy handle" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div><span className="text-white/60">Phone: </span><span className="text-gold font-medium">{instructions.phone || "—"}</span></div>
          <CopyChip value={instructions.phone} label="Copy phone" />
        </div>
        <p className="text-white/70 leading-relaxed pt-2 border-t border-white/10">{instructions.text}</p>
      </div>
    );
  }
  if (method === "crypto") {
    return (
      <div className="space-y-3">
        {(instructions.wallets || []).map((w, i) => (
          <div key={i} className="rounded-md border border-gold/20 bg-black/40 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-gold font-semibold">{w.label || w.symbol}</div>
              <span className="text-xs text-white/50 uppercase tracking-wider">{w.network}</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-white/85 break-all bg-black/60 rounded px-2 py-1.5 border border-white/5" data-testid={`crypto-addr-${w.symbol?.toLowerCase()}`}>{w.address}</code>
              <CopyChip value={w.address} label="Copy" />
            </div>
          </div>
        ))}
        <p className="text-xs text-white/60 leading-relaxed">{instructions.text}</p>
      </div>
    );
  }
  // bank_transfer / wire / cash — plain text
  return (
    <div className="text-sm text-white/85 leading-relaxed whitespace-pre-line">
      {instructions.text}
    </div>
  );
}

export default function ManualPaymentModal({
  open, onClose,
  amount, currency = "JMD", purpose = "other", purposeRefId = null,
  defaultNote = "", onApproved, "data-testid": tid = "manual-payment-modal",
}) {
  const [methods, setMethods] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [intent, setIntent] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [receiptRequired, setReceiptRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState(defaultNote || "");

  // Pull active methods on mount.
  useEffect(() => {
    if (!open) return;
    api.get("/payments/manual/settings").then((r) => {
      setMethods(r.data?.methods || Object.keys(METHOD_META));
    }).catch(() => setMethods(Object.keys(METHOD_META)));
  }, [open]);

  const reset = () => {
    setChosen(null); setIntent(null); setInstructions(null);
    setReceiptRequired(false); setSubmitting(false); setUploading(false);
  };

  const handleClose = () => { reset(); onClose && onClose(); };

  const createIntent = async (method) => {
    if (!amount || amount <= 0) { toast.error("Amount missing"); return; }
    setChosen(method); setSubmitting(true);
    try {
      const res = await api.post("/payments/manual/intent", {
        method, amount, currency, purpose,
        purpose_ref_id: purposeRefId || null,
        note: note || `${purpose} payment`,
      });
      setIntent(res.data.intent);
      setInstructions(res.data.instructions);
      setReceiptRequired(!!res.data.receipt_required);
    } catch (err) {
      toast.error(errorMessage(err, "Could not create payment"));
      setChosen(null);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadReceipt = async (file) => {
    if (!file || !intent) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/payments/manual/${intent.id}/upload-receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receipt uploaded — admin will verify within 24h.");
      onApproved && onApproved(intent);
      handleClose();
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const skipReceipt = () => {
    toast.info("Saved. Pay in person and admin will mark it received.");
    onApproved && onApproved(intent);
    handleClose();
  };

  const visibleMethods = useMemo(() => methods.filter((m) => METHOD_META[m]), [methods]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={tid}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
            className="bg-zinc-950 border border-gold/25 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-zinc-950/95 backdrop-blur border-b border-gold/15 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-gold" />
                <div>
                  <div className="text-gold font-display text-lg">Manual Payment</div>
                  <div className="text-xs text-white/60">{currency} ${Number(amount || 0).toLocaleString()} · {purpose.replace("_", " ")}</div>
                </div>
              </div>
              <button data-testid="manual-pay-close" onClick={handleClose} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {!intent && (
                <>
                  <p className="text-sm text-white/70 mb-4">
                    Choose how you'd like to pay. After we receive it (within 24h), your purchase unlocks automatically.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {visibleMethods.map((m) => {
                      const meta = METHOD_META[m]; const I = meta.Icon;
                      return (
                        <button
                          key={m} data-testid={`manual-method-${m}`}
                          disabled={submitting && chosen !== m}
                          onClick={() => createIntent(m)}
                          className={`flex items-start gap-3 text-left rounded-md border p-4 transition-all ${
                            chosen === m
                              ? "border-gold bg-gold/10"
                              : "border-white/10 hover:border-gold/40 hover:bg-white/[0.02]"
                          } disabled:opacity-50`}
                        >
                          <I className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="text-white text-sm font-medium">{meta.label}</div>
                            <div className="text-xs text-white/50 mt-0.5">{meta.blurb}</div>
                            {submitting && chosen === m && (
                              <div className="flex items-center gap-1.5 text-xs text-gold/80 mt-2">
                                <Loader2 className="w-3 h-3 animate-spin" /> Generating instructions…
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-5">
                    <label className="text-xs text-white/60">Reference (optional)</label>
                    <input
                      data-testid="manual-pay-note"
                      type="text" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Yacht Gala — Sarah Brown"
                      className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    />
                  </div>
                </>
              )}

              {intent && (
                <>
                  <div className="rounded-md border border-gold/20 bg-black/40 p-4 mb-4">
                    <div className="text-xs uppercase tracking-wider text-gold/70 mb-2">
                      {METHOD_META[intent.method]?.label || intent.method}
                    </div>
                    <InstructionsBlock method={intent.method} instructions={instructions} />
                  </div>

                  <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 mb-4 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-100/80">
                      Your reference ID: <code className="text-amber-300">{intent.id.slice(0, 8)}</code>.
                      Include this (or your name) in the transfer memo so admin can match it.
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-sm text-white/80 font-medium flex items-center gap-2">
                      <FileImage className="w-4 h-4 text-gold" />
                      Upload receipt {receiptRequired ? "(required)" : "(optional)"}
                    </div>
                    <label
                      data-testid="manual-pay-upload"
                      className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gold/30 hover:border-gold/60 rounded-md py-8 cursor-pointer transition-colors"
                    >
                      <input
                        type="file" accept=".jpg,.jpeg,.png,.webp,.pdf"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadReceipt(f);
                        }}
                      />
                      {uploading ? (
                        <><Loader2 className="w-6 h-6 text-gold animate-spin" /><div className="text-xs text-white/60">Uploading…</div></>
                      ) : (
                        <><Upload className="w-6 h-6 text-gold" /><div className="text-sm text-white/70">Drop screenshot or click to choose</div><div className="text-xs text-white/40">JPG, PNG, WEBP or PDF · max 10 MB</div></>
                      )}
                    </label>
                    {!receiptRequired && (
                      <button
                        data-testid="manual-pay-skip-receipt"
                        onClick={skipReceipt}
                        className="w-full text-sm text-white/60 hover:text-gold border border-white/10 hover:border-gold/40 rounded-md py-2.5 transition-colors"
                      >
                        I'll pay later / on completion
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
